import { FC, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import {
    useAiRequestLogDetail,
    useAiRequestLogs,
    useUpdateAiRequestLogResolution,
} from '../../hooks/useAiLog';

const PROVIDER_OPTIONS = [
    { value: '', label: '전체' },
    { value: 'OPENAI', label: 'OPENAI' },
    { value: 'CLAUDE', label: 'CLAUDE' },
    { value: 'GEMINI', label: 'GEMINI' },
];

const RESULT_OPTIONS = [
    { value: 'ALL', label: '전체' },
    { value: 'SUCCESS', label: '성공' },
    { value: 'FAIL', label: '실패' },
];

const WORKFLOW_OPTIONS = [
    { value: 'ALL', label: '전체' },
    { value: 'WORKFLOW', label: '.workflow만' },
    { value: 'NON_WORKFLOW', label: '일반 요청만' },
];

const RESOLUTION_OPTIONS = [
    { value: 'ALL', label: '해결상태 전체' },
    { value: 'OPEN', label: '미해결' },
    { value: 'RESOLVED', label: '해결됨' },
];

export const AiLogList: FC = () => {
    const [searchParams] = useSearchParams();
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [provider, setProvider] = useState('');
    const [requestType, setRequestType] = useState('');
    const [externalBatchId, setExternalBatchId] = useState(searchParams.get('externalBatchId') ?? '');
    const [resultFilter, setResultFilter] = useState<'ALL' | 'SUCCESS' | 'FAIL'>('ALL');
    const [workflowFilter, setWorkflowFilter] = useState<'ALL' | 'WORKFLOW' | 'NON_WORKFLOW'>('ALL');
    const [resolutionFilter, setResolutionFilter] = useState<'ALL' | 'OPEN' | 'RESOLVED'>('ALL');
    const [userIdInput, setUserIdInput] = useState('');
    const [selectedLogId, setSelectedLogId] = useState<number | undefined>(undefined);

    const { mutateAsync: updateResolution, isPending: isUpdatingResolution } =
        useUpdateAiRequestLogResolution();
    const { data: logDetail, isLoading: detailLoading } = useAiRequestLogDetail(selectedLogId);

    const isSuccess = resultFilter === 'ALL' ? undefined : resultFilter === 'SUCCESS';
    const workflowOnly = workflowFilter === 'ALL' ? undefined : workflowFilter === 'WORKFLOW';
    const resolutionStatus = resolutionFilter === 'ALL' ? undefined : resolutionFilter;
    const userId = userIdInput.trim() ? Number(userIdInput) : undefined;

    const { data, isLoading, error } = useAiRequestLogs({
        page: page + 1,
        size,
        provider: provider || undefined,
        requestType: requestType.trim() || undefined,
        externalBatchId: externalBatchId.trim() || undefined,
        workflowOnly,
        isSuccess,
        resolutionStatus,
        userId: Number.isFinite(userId) ? userId : undefined,
    });

    const handleResolutionClick = async (id: number, currentlyResolved: boolean) => {
        if (currentlyResolved) {
            if (!window.confirm('해결 상태를 다시 미해결로 되돌릴까요?')) return;
            await updateResolution({ logId: id, data: { resolved: false } });
            return;
        }
        const note = window.prompt('수정/반영 메모를 남길 수 있습니다. (선택)');
        if (note === null) return;
        await updateResolution({
            logId: id,
            data: { resolved: true, resolutionNote: note?.trim() || undefined },
        });
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 3 }}>
                AI 요청 로그
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <TextField
                    select
                    label="Provider"
                    size="small"
                    value={provider}
                    onChange={(e) => {
                        setProvider(e.target.value);
                        setPage(0);
                    }}
                    sx={{ minWidth: 140 }}
                >
                    {PROVIDER_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    label="Request Type"
                    size="small"
                    value={requestType}
                    onChange={(e) => {
                        setRequestType(e.target.value);
                        setPage(0);
                    }}
                    sx={{ minWidth: 220 }}
                    placeholder="generateWords"
                />

                <TextField
                    label="External Batch ID"
                    size="small"
                    value={externalBatchId}
                    onChange={(e) => {
                        setExternalBatchId(e.target.value);
                        setPage(0);
                    }}
                    sx={{ minWidth: 240 }}
                    placeholder="batch_... / operations/..."
                />

                <TextField
                    select
                    label="결과"
                    size="small"
                    value={resultFilter}
                    onChange={(e) => {
                        setResultFilter(e.target.value as 'ALL' | 'SUCCESS' | 'FAIL');
                        setPage(0);
                    }}
                    sx={{ minWidth: 130 }}
                >
                    {RESULT_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    select
                    label="워크플로우"
                    size="small"
                    value={workflowFilter}
                    onChange={(e) => {
                        setWorkflowFilter(e.target.value as 'ALL' | 'WORKFLOW' | 'NON_WORKFLOW');
                        setPage(0);
                    }}
                    sx={{ minWidth: 150 }}
                >
                    {WORKFLOW_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    select
                    label="해결상태"
                    size="small"
                    value={resolutionFilter}
                    onChange={(e) => {
                        setResolutionFilter(e.target.value as 'ALL' | 'OPEN' | 'RESOLVED');
                        setPage(0);
                    }}
                    sx={{ minWidth: 150 }}
                >
                    {RESOLUTION_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    label="User ID"
                    size="small"
                    value={userIdInput}
                    onChange={(e) => {
                        setUserIdInput(e.target.value);
                        setPage(0);
                    }}
                    sx={{ width: 120 }}
                />
            </Box>

            {isLoading && <CircularProgress />}
            {error && <Alert severity="error">{error.message}</Alert>}

            {data && (
                <Card>
                    <CardContent sx={{ p: 0 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>시각</TableCell>
                                    <TableCell>결과</TableCell>
                                    <TableCell>Provider/Model</TableCell>
                                    <TableCell>요청 타입</TableCell>
                                    <TableCell>External Batch</TableCell>
                                    <TableCell>사용자</TableCell>
                                    <TableCell>토큰/지연</TableCell>
                                    <TableCell>처리상태</TableCell>
                                    <TableCell>에러</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(data.content ?? []).map((log) => (
                                    <TableRow
                                        key={log.id}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => setSelectedLogId(log.id)}
                                    >
                                        <TableCell>{log.id}</TableCell>
                                        <TableCell>
                                            <Typography variant="caption">
                                                {log.createdAt?.replace('T', ' ').slice(0, 19)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                color={log.isSuccess ? 'success' : 'error'}
                                                label={log.isSuccess ? '성공' : '실패'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{log.provider}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {log.modelName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{log.requestType}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {log.requestUri || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption">
                                                {log.externalBatchId || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{log.userId ?? '-'}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {log.userNickname || log.userEmail || log.firebaseUid || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption">
                                                in:{log.inputTokens} / out:{log.outputTokens} / total:
                                                {log.totalTokens}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                display="block"
                                                color="text.secondary"
                                            >
                                                {log.timeTakenMs} ms
                                            </Typography>
                                        </TableCell>
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            {log.isSuccess ? (
                                                <Typography variant="caption" color="text.secondary">
                                                    -
                                                </Typography>
                                            ) : (
                                                <>
                                                    <Chip
                                                        size="small"
                                                        color={
                                                            log.resolutionStatus === 'RESOLVED'
                                                                ? 'success'
                                                                : 'default'
                                                        }
                                                        label={
                                                            log.resolutionStatus === 'RESOLVED'
                                                                ? '해결됨'
                                                                : '미해결'
                                                        }
                                                        sx={{ mb: 0.5 }}
                                                    />
                                                    {log.resolutionNote && (
                                                        <Typography
                                                            variant="caption"
                                                            display="block"
                                                            color="text.secondary"
                                                        >
                                                            {log.resolutionNote}
                                                        </Typography>
                                                    )}
                                                    {log.resolvedAt && (
                                                        <Typography
                                                            variant="caption"
                                                            display="block"
                                                            color="text.secondary"
                                                        >
                                                            {log.resolvedAt.replace('T', ' ').slice(0, 19)}
                                                        </Typography>
                                                    )}
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        disabled={isUpdatingResolution}
                                                        onClick={() =>
                                                            handleResolutionClick(
                                                                log.id,
                                                                log.resolutionStatus === 'RESOLVED',
                                                            )
                                                        }
                                                    >
                                                        {log.resolutionStatus === 'RESOLVED'
                                                            ? '다시열기'
                                                            : '해결처리'}
                                                    </Button>
                                                </>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="caption"
                                                color={log.isSuccess ? 'text.secondary' : 'error'}
                                            >
                                                {log.errorMessage ||
                                                    (log.httpStatus ? `HTTP ${log.httpStatus}` : '-')}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {(data.content ?? []).length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                                            <Typography color="text.secondary">
                                                로그가 없습니다.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={data.totalCount ?? 0}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={size}
                            onRowsPerPageChange={(e) => {
                                setSize(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[10, 20, 50, 100]}
                        />
                    </CardContent>
                </Card>
            )}

            <Dialog
                open={!!selectedLogId}
                onClose={() => setSelectedLogId(undefined)}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="h6">AI 요청 상세</Typography>
                        {logDetail && (
                            <Chip
                                size="small"
                                color={logDetail.isSuccess ? 'success' : 'error'}
                                label={logDetail.isSuccess ? '성공' : '실패'}
                            />
                        )}
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    {detailLoading && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {logDetail && (
                        <Stack spacing={2.5}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        ID
                                    </Typography>
                                    <Typography variant="body2">{logDetail.id}</Typography>
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        시각
                                    </Typography>
                                    <Typography variant="body2">
                                        {logDetail.createdAt?.replace('T', ' ').slice(0, 19)}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Provider
                                    </Typography>
                                    <Typography variant="body2">{logDetail.provider}</Typography>
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Model
                                    </Typography>
                                    <Typography variant="body2">{logDetail.modelName}</Typography>
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        요청 타입
                                    </Typography>
                                    <Typography variant="body2">{logDetail.requestType}</Typography>
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        External Batch ID
                                    </Typography>
                                    <Typography variant="body2">
                                        {logDetail.externalBatchId || '-'}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        URI
                                    </Typography>
                                    <Typography variant="body2">
                                        {logDetail.requestUri || '-'}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        사용자
                                    </Typography>
                                    <Typography variant="body2">
                                        {logDetail.userId ?? '-'}
                                        {logDetail.userNickname
                                            ? ` (${logDetail.userNickname})`
                                            : logDetail.userEmail
                                                ? ` (${logDetail.userEmail})`
                                                : ''}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        HTTP Status
                                    </Typography>
                                    <Typography variant="body2">{logDetail.httpStatus ?? '-'}</Typography>
                                </Grid>
                            </Grid>

                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    토큰 / 성능
                                </Typography>
                                <Stack direction="row" spacing={2} flexWrap="wrap">
                                    <Chip
                                        label={`Input: ${logDetail.inputTokens.toLocaleString()}`}
                                        size="small"
                                        variant="outlined"
                                    />
                                    <Chip
                                        label={`Output: ${logDetail.outputTokens.toLocaleString()}`}
                                        size="small"
                                        variant="outlined"
                                    />
                                    <Chip
                                        label={`Total: ${logDetail.totalTokens.toLocaleString()}`}
                                        size="small"
                                        variant="outlined"
                                    />
                                    <Chip
                                        label={`${logDetail.timeTakenMs.toLocaleString()} ms`}
                                        size="small"
                                        variant="outlined"
                                        color="info"
                                    />
                                </Stack>
                            </Box>

                            {logDetail.errorMessage && (
                                <Alert severity="error" variant="outlined">
                                    <Typography variant="subtitle2" gutterBottom>
                                        에러 메시지
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {logDetail.errorMessage}
                                    </Typography>
                                </Alert>
                            )}

                            {!logDetail.isSuccess && (
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                        해결 상태
                                    </Typography>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Chip
                                            size="small"
                                            color={
                                                logDetail.resolutionStatus === 'RESOLVED'
                                                    ? 'success'
                                                    : 'default'
                                            }
                                            label={
                                                logDetail.resolutionStatus === 'RESOLVED'
                                                    ? '해결됨'
                                                    : '미해결'
                                            }
                                        />
                                        {logDetail.resolutionNote && (
                                            <Typography variant="body2" color="text.secondary">
                                                {logDetail.resolutionNote}
                                            </Typography>
                                        )}
                                        {logDetail.resolvedAt && (
                                            <Typography variant="caption" color="text.secondary">
                                                {logDetail.resolvedAt.replace('T', ' ').slice(0, 19)}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Box>
                            )}

                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    Input (User Prompt)
                                </Typography>
                                <Box component="pre" sx={jsonPreStyle}>
                                    {formatPromptData(logDetail.userPrompt)}
                                </Box>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    Output (AI 응답)
                                </Typography>
                                <Box component="pre" sx={jsonPreStyle}>
                                    {formatPromptData(logDetail.outputData)}
                                </Box>
                            </Box>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedLogId(undefined)}>닫기</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const jsonPreStyle = {
    p: 1.5,
    bgcolor: '#f5f5f5',
    border: '1px solid #e0e0e0',
    borderRadius: 1,
    overflowX: 'auto',
    fontSize: 12,
    lineHeight: 1.5,
    maxHeight: 400,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
};

const formatPromptData = (value?: string | null): string => {
    if (!value) return '(데이터 없음)';
    try {
        return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
        return value;
    }
};
